import {gql, QueryOptions} from '@apollo/client';
import Backbone from 'backbone';

import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ApolloFileState from 'filestate/ApolloFileState';
import {FileStateAttributes} from 'filestate/FileState';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as navigation from 'Navigation';

import {
  getBenchlingAppFileStateQueryVariables,
  getBenchlingAppFileStateQuery,
} from './__generated/getBenchlingAppFileStateQuery';
import BenchlingAppTabState from './BenchlingAppTabState';

type BenchlingAppFileStateBenchlingApp = getBenchlingAppFileStateQuery['benchlingApp'];

class BenchlingAppToolbarState extends Backbone.Model {}

export const BENCHLING_APP_FILE_STATE_QUERY = gql`
  query getBenchlingAppFileStateQuery($benchlingAppId: StrictString!)
  @skipAudit
  @engComponent(name: BENCHLING_APPS) {
    benchlingApp(id: $benchlingAppId) {
      ... on BenchlingAppNode {
        ...BenchlingAppGraphQL
      }
    }
  }
  ${getFragmentWrapper('BenchlingApp').fragment}
`;

interface BenchlingAppFileStateAttributes {
  // These attributes are optional - they may be undefined if app is loading or inaccessible
  benchlingApp?: BenchlingAppFileStateBenchlingApp;
  name?: string;
  url?: string;
}

class BenchlingAppFileState extends ApolloFileState<
  BenchlingAppFileStateBenchlingApp,
  getBenchlingAppFileStateQueryVariables,
  BenchlingAppFileStateAttributes
> {
  static fileType = ModelTypeConstants.BENCHLING_APP;

  static override getQueryOptions(fileId: string): QueryOptions<getBenchlingAppFileStateQueryVariables> {
    return {
      query: BENCHLING_APP_FILE_STATE_QUERY,
      variables: {
        benchlingAppId: fileId,
      },
    };
  }

  static override dataToAttributes(data: getBenchlingAppFileStateQuery): BenchlingAppFileStateAttributes {
    if (isInaccessible(data.benchlingApp)) {
      return {};
    }
    const benchlingApp = data.benchlingApp;
    return {benchlingApp, name: benchlingApp.name, url: benchlingApp.url};
  }

  static override openFile(fileId: string): JQueryPromise<BenchlingAppFileState> {
    return super.openFile(fileId) as JQueryPromise<BenchlingAppFileState>;
  }

  override defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new BenchlingAppToolbarState(),
      statusbar: new statusbar.SingleBufferStatusBarState(),
      hideTabNav: true,
    };
  }

  override initialize(...args: Array<unknown>): void {
    super.initialize(...args);

    const buffers = new navigation.BufferStates([
      new navigation.BufferState(),
      new navigation.BufferState({active: false}),
    ]);
    this.set({buffers});
    buffers.addTab(
      new BenchlingAppTabState({
        benchlingAppId: this.id,
      }),
      {active: true}
    );
  }

  // String if app readable, undefined otherwise (app inaccessible)
  override editURL(): string | undefined {
    return this.get('url');
  }

  override name(): string {
    return this.get('name') || 'Loading...';
  }

  override registerTitleAndURLSync(): void {
    this.on('change:name change:url', () => {
      this.syncTitleAndURL();
    });
  }

  override hasUnsavedChanges(): boolean {
    return false;
  }

  override cleanUp(): void {
    super.cleanUp();
  }
}

export default BenchlingAppFileState;
